import { useState } from 'react';

export default function WhatsAppAlerts() {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    phone_number: '',
    departements: [] as string[]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const departements = [
    '01 - Ain', '02 - Aisne', '03 - Allier', '04 - Alpes-de-Haute-Provence', '05 - Hautes-Alpes',
    '06 - Alpes-Maritimes', '07 - Ardèche', '08 - Ardennes', '09 - Ariège', '10 - Aube',
    '11 - Aude', '12 - Aveyron', '13 - Bouches-du-Rhône', '14 - Calvados', '15 - Cantal',
    '16 - Charente', '17 - Charente-Maritime', '18 - Cher', '19 - Corrèze', '2A - Corse-du-Sud',
    '2B - Haute-Corse', '21 - Côte-d\'Or', '22 - Côtes-d\'Armor', '23 - Creuse', '24 - Dordogne',
    '25 - Doubs', '26 - Drôme', '27 - Eure', '28 - Eure-et-Loir', '29 - Finistère',
    '30 - Gard', '31 - Haute-Garonne', '32 - Gers', '33 - Gironde', '34 - Hérault',
    '35 - Ille-et-Vilaine', '36 - Indre', '37 - Indre-et-Loire', '38 - Isère', '39 - Jura',
    '40 - Landes', '41 - Loir-et-Cher', '42 - Loire', '43 - Haute-Loire', '44 - Loire-Atlantique',
    '45 - Loiret', '46 - Lot', '47 - Lot-et-Garonne', '48 - Lozère', '49 - Maine-et-Loire',
    '50 - Manche', '51 - Marne', '52 - Haute-Marne', '53 - Mayenne', '54 - Meurthe-et-Moselle',
    '55 - Meuse', '56 - Morbihan', '57 - Moselle', '58 - Nièvre', '59 - Nord',
    '60 - Oise', '61 - Orne', '62 - Pas-de-Calais', '63 - Puy-de-Dôme', '64 - Pyrénées-Atlantiques',
    '65 - Hautes-Pyrénées', '66 - Pyrénées-Orientales', '67 - Bas-Rhin', '68 - Haut-Rhin', '69 - Rhône',
    '70 - Haute-Saône', '71 - Saône-et-Loire', '72 - Sarthe', '73 - Savoie', '74 - Haute-Savoie',
    '75 - Paris', '76 - Seine-Maritime', '77 - Seine-et-Marne', '78 - Yvelines', '79 - Deux-Sèvres',
    '80 - Somme', '81 - Tarn', '82 - Tarn-et-Garonne', '83 - Var', '84 - Vaucluse',
    '85 - Vendée', '86 - Vienne', '87 - Haute-Vienne', '88 - Vosges', '89 - Yonne',
    '90 - Territoire de Belfort', '91 - Essonne', '92 - Hauts-de-Seine', '93 - Seine-Saint-Denis', 
    '94 - Val-de-Marne', '95 - Val-d\'Oise', '971 - Guadeloupe', '972 - Martinique', '973 - Guyane',
    '974 - La Réunion', '976 - Mayotte'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('nom', formData.nom);
      formBody.append('email', formData.email);
      formBody.append('phone_number', formData.phone_number);
      formBody.append('departements', formData.departements.join(', '));

      const response = await fetch('https://readdy.ai/api/form/d4t8aacapcs36ntc0aq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ nom: '', email: '', phone_number: '', departements: [] });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDepartementChange = (departement: string) => {
    setFormData(prev => ({
      ...prev,
      departements: prev.departements.includes(departement)
        ? prev.departements.filter(d => d !== departement)
        : [...prev.departements, departement]
    }));
  };

  return (
    <section id="whatsapp-alerts" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-3 rounded-full mb-6 shadow-lg">
            <i className="ri-whatsapp-line text-2xl"></i>
            <span className="font-bold text-lg">Nouveau Service</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Recevez les Nouvelles Demandes sur WhatsApp
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Soyez alerté instantanément dès qu'une nouvelle demande correspond à votre zone d'intervention
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left: Benefits */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-notification-3-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Alertes Instantanées</h3>
                  <p className="text-gray-600">Recevez une notification WhatsApp dès qu'une nouvelle demande est disponible dans votre zone</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Ciblage Géographique</h3>
                  <p className="text-gray-600">Choisissez vos départements et recevez uniquement les demandes qui vous intéressent</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Réactivité Maximale</h3>
                  <p className="text-gray-600">Soyez le premier à contacter le client et augmentez vos chances de conversion</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">100% Gratuit</h3>
                  <p className="text-gray-600">Service d'alertes entièrement gratuit, sans engagement ni frais cachés</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Subscription Form */}
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 shadow-xl border border-teal-100">
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-500 rounded-2xl flex items-center justify-center">
                  <i className="ri-whatsapp-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Inscription aux Alertes</h3>
                  <p className="text-gray-600 text-sm">Gratuit et sans engagement</p>
                </div>
              </div>

              <form id="whatsapp-alerts" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="nom" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom Complet *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="Jean Dupont"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="jean.dupont@exemple.fr"
                  />
                </div>

                <div>
                  <label htmlFor="phone_number" className="block text-sm font-semibold text-gray-700 mb-2">
                    Numéro WhatsApp *
                  </label>
                  <input
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder="06 12 34 56 78"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format français (06 XX XX XX XX)</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Sélectionnez vos Départements * ({formData.departements.length} sélectionné{formData.departements.length > 1 ? 's' : ''})
                  </label>
                  <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-y-auto bg-white">
                    <div className="grid grid-cols-1 gap-2">
                      {departements.map((departement) => (
                        <label key={departement} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded text-sm">
                          <input
                            type="checkbox"
                            checked={formData.departements.includes(departement)}
                            onChange={() => handleDepartementChange(departement)}
                            className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                          />
                          <span className="text-gray-700">{departement}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Vous pouvez sélectionner plusieurs départements</p>
                  {formData.departements.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">Veuillez sélectionner au moins un département</p>
                  )}
                </div>

                {formData.departements.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-700 font-medium mb-2">Départements sélectionnés :</p>
                    <div className="flex flex-wrap gap-1">
                      {formData.departements.map((dept) => (
                        <span key={dept} className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                          {dept.split(' - ')[0]}
                          <button
                            type="button"
                            onClick={() => handleDepartementChange(dept)}
                            className="text-green-600 hover:text-green-800"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                    <i className="ri-check-line text-green-600 text-xl"></i>
                    <p className="text-sm text-green-700">Parfait ! Vous êtes inscrit aux alertes WhatsApp pour {formData.departements.length} département{formData.departements.length > 1 ? 's' : ''}. Vous recevrez une notification dès qu'une nouvelle demande sera disponible.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <i className="ri-error-warning-line text-red-600 text-xl"></i>
                    <p className="text-sm text-red-700">Une erreur est survenue. Veuillez réessayer.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || formData.departements.length === 0}
                  className="w-full bg-green-600 text-white px-6 py-4 rounded-lg text-base font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Inscription en cours...
                    </>
                  ) : (
                    <>
                      <i className="ri-whatsapp-line"></i>
                      S'inscrire aux Alertes
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-shield-check-line text-green-600"></i>
                  <span>Service gratuit • Données sécurisées • Désinscription possible</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold text-center mb-12">Comment ça marche ?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-white/30">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h4 className="text-xl font-bold mb-2">Inscrivez-vous</h4>
              <p className="text-blue-100">Remplissez le formulaire avec votre numéro WhatsApp et vos départements</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-white/30">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h4 className="text-xl font-bold mb-2">Recevez les Alertes</h4>
              <p className="text-blue-100">Dès qu'une nouvelle demande correspond à votre zone, vous recevez une notification</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-white/30">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h4 className="text-xl font-bold mb-2">Contactez le Client</h4>
              <p className="text-blue-100">Accédez rapidement à la demande et contactez le client avant vos concurrents</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}